import React, { Component } from "react"
import { graphql } from "@apollo/client/react/hoc"
import gql from "graphql-tag"

import DocumentList from "./DocumentList"
import ConfirmationPopup from "../../Material/ConfirmationPopup"
import withApolloClient from "../../withApolloClient"
import { HasPermissionsQuery } from "../../Functions/querys"
import "./DocumentsTab.css"

const IncidentDocumentOwnersQuery = gql`
    query IncidentDocumentOwnersQuery($incidentId: ID!, $includeCounts: Boolean!) {
        incident(incidentId: $incidentId) @include(if: $includeCounts) {
            id
            driver {
                id
            }
            car {
                id
            }
            incidentDocumentsCount: documentCount(slugKey: ["INCIDENT"])
            partyDocumentsCount: documentCount(slugKey: ["PARTY"])
            claimDocumentsCount: documentCount(slugKey: ["LIABILITY", "SUBROGATION"])
            inspectionDocumentsCount: documentCount(slugKey: ["INSPECTION"])
            fleetSyncCarDocumentsCount: documentCount(slugKey: ["CAR"])
            driverDocumentsCount
            carDocumentsCount
        }
        allIncidentParties(incidentId: $incidentId) {
            edges {
                node {
                    id
                    pk
                    documentSlug
                    partyName
                    partyType
                }
            }
        }
        allLiabilityClaims(incidentId: $incidentId) {
            edges {
                node {
                    id
                    pk
                    documentSlug
                    externalId
                    tpa {
                        name
                    }
                }
            }
        }
        allSubrogationClaims(incidentId: $incidentId) {
            edges {
                node {
                    id
                    pk
                    documentSlug
                    externalId
                    tpa {
                        name
                    }
                }
            }
        }
    }
`

const RefetchIncidentCarDocumentsFromFleetsyncMutation = gql`
    mutation RefetchIncidentCarDocumentsFromFleetsync($input: RefetchIncidentCarDocumentsFromFleetSyncMutationInput!) {
        refetchIncidentCarDocumentsFromFleetsync(input: $input) {
            ok
            errors {
                field
                messages
            }
        }
    }
`

class DocumentListContainer extends Component {
    constructor(props) {
        super(props)
        this.state = {
            isArchived: false,
            selectedSection: "general",
            selectedCarDocumentView: "car",
            refetchFleetSyncDocumentsModalOpen: false,
            refetchFleetSyncDocumentsLoading: false,
            refetchFleetSyncDocumentsError: "",
        }
    }

    getDocumentOwnerMap = () => {
        const ownerMap = {}

        const partyNodes = this.props.allIncidentParties?.edges?.map(({ node }) => node) || []
        partyNodes.forEach((node) => {
            if (node?.documentSlug) {
                ownerMap[node.documentSlug] = {
                    kind: "party",
                    label: `${node.pk || node.id} - ${node.partyName || "Unknown Party"} - ${node.partyType || "Unknown"}`,
                }
            }
        })

        const liabilityNodes = this.props.allLiabilityClaims?.edges?.map(({ node }) => node) || []
        liabilityNodes.forEach((node) => {
            if (node?.documentSlug) {
                ownerMap[node.documentSlug] = {
                    kind: "liability",
                    label: `${node.pk || node.id} - ${node.externalId || "No External ID"} - ${node.tpa?.name || "--"}`,
                }
            }
        })

        const subrogationNodes = this.props.allSubrogationClaims?.edges?.map(({ node }) => node) || []
        subrogationNodes.forEach((node) => {
            if (node?.documentSlug) {
                ownerMap[node.documentSlug] = {
                    kind: "subrogation",
                    label: `${node.pk || node.id} - ${node.externalId || "No External ID"} - ${node.tpa?.name || "--"}`,
                }
            }
        })

        return ownerMap
    }

    renderArchiveViewSelector = () => {
        const { isArchived } = this.state
        return (
            <div className="documents-view-toggle-wrap">
                <span className="documents-view-toggle-text">Exclude Archived</span>
                <label className="documents-archive-switch" aria-label="Toggle archived documents">
                    <input
                        type="checkbox"
                        checked={isArchived}
                        onChange={(e) => this.setState({ isArchived: e.target.checked })}
                    />
                    <span className="documents-archive-slider" />
                </label>
                <span className="documents-view-toggle-text">Show Archived</span>
            </div>
        )
    }

    refetchFleetSyncDocuments = async () => {
        this.setState({ refetchFleetSyncDocumentsLoading: true, refetchFleetSyncDocumentsError: "" })
        try {
            const result = await this.props.apolloClient.mutate({
                mutation: RefetchIncidentCarDocumentsFromFleetsyncMutation,
                variables: { input: { incidentId: this.props.id } },
                refetchQueries: ["IncidentDocumentOwnersQuery", "AllIncidentDocuments"],
                awaitRefetchQueries: true,
            })
            const response = result?.data?.refetchIncidentCarDocumentsFromFleetsync
            if (response?.ok) {
                this.setState({ refetchFleetSyncDocumentsModalOpen: false })
            } else {
                this.setState({
                    refetchFleetSyncDocumentsError: response?.errors?.[0]?.messages?.[0] || "Unable to refetch FleetSync documents.",
                })
            }
        } catch (error) {
            this.setState({ refetchFleetSyncDocumentsError: error.message })
        } finally {
            this.setState({ refetchFleetSyncDocumentsLoading: false })
        }
    }

    renderScopedView = () => {
        const { id, refetchQuery, viewType, scope, slug, categoryId } = this.props
        const normalizedScope = (scope || "PARTY").toUpperCase()
        const isOtherDocumentScope = ["INSPECTION", "DRIVER", "CAR"].includes(normalizedScope)
        const category = isOtherDocumentScope ? null : normalizedScope
        const slugKey = normalizedScope === "INCIDENT" ? ["INCIDENT"] : normalizedScope === "CLAIM" ? ["LIABILITY", "SUBROGATION"] : null
        const documentOwnerMap = this.getDocumentOwnerMap()
        const otherDocumentsCategory = isOtherDocumentScope ? normalizedScope.toLowerCase() : null
        const otherDocumentsCategoryId = categoryId || (normalizedScope === "INSPECTION" ? id : null)
        return (
            <div className="document-tab-container" style={{ borderTopLeftRadius: "0px" }}>
                <DocumentList
                    id={id}
                    slug={slug || null}
                    isArchived={this.state.isArchived}
                    slugKey={slugKey}
                    documentOwnerMap={documentOwnerMap}
                    requireTargetSelection={normalizedScope === "INCIDENT"}
                    refetchQuery={refetchQuery || null}
                    viewType={viewType || null}
                    category={category}
                    showDocumentActions={!isOtherDocumentScope}
                    showIncidentDocuments={!isOtherDocumentScope}
                    showOtherDocumentsSection={isOtherDocumentScope}
                    otherDocumentsCategory={otherDocumentsCategory}
                    otherDocumentsCategoryId={otherDocumentsCategoryId}
                    archiveFilterControl={!isOtherDocumentScope ? this.renderArchiveViewSelector() : null}
                />
            </div>
        )
    }

    renderFullIncidentView = () => {
        const { id, refetchQuery, viewType } = this.props
        const documentOwnerMap = this.getDocumentOwnerMap()
        const counts = this.props.documentCounts || {}

        const carDocumentsConfig = this.state.selectedCarDocumentView === "fleetSync"
            ? { category: null, slugKey: ["CAR"], showOtherDocuments: false, showDocumentActions: false, showArchiveFilter: false, incidentDocumentPreviewContext: { objectType: "car", isPicture: true } }
            : { category: null, slugKey: null, showOtherDocuments: true, otherDocumentsCategory: "car", otherDocumentsCategoryId: this.props.incidentCarId }

        const sectionConfigMap = {
            general: { category: "INCIDENT", slugKey: ["INCIDENT"], showOtherDocuments: false },
            party: { category: "PARTY", slugKey: ["PARTY"], showOtherDocuments: false },
            claim: { category: "CLAIM", slugKey: ["LIABILITY", "SUBROGATION"], showOtherDocuments: false },
            inspection: { category: null, slugKey: null, showOtherDocuments: true, otherDocumentsCategory: "inspection", otherDocumentsCategoryId: id },
            driver: { category: null, slugKey: null, showOtherDocuments: true, otherDocumentsCategory: "driver", otherDocumentsCategoryId: this.props.incidentDriverId },
            car: carDocumentsConfig,
        }
        const selectedSectionConfig = sectionConfigMap[this.state.selectedSection] || sectionConfigMap.general
        const showDocumentActions = selectedSectionConfig.showDocumentActions !== false && !selectedSectionConfig.showOtherDocuments
        const showArchiveFilter = selectedSectionConfig.showArchiveFilter !== false && !selectedSectionConfig.showOtherDocuments
        const showSyncFleetSyncDocuments = this.state.selectedSection === "car" && this.state.selectedCarDocumentView === "fleetSync" && this.props.hasPermissions?.includes("custom_can_refetch_car_documents_from_fleetsync")

        return (
            <div className="document-tab-container incident-docs-unified" style={{ borderTopLeftRadius: "0px" }}>
                {this.state.refetchFleetSyncDocumentsModalOpen && (
                    <ConfirmationPopup
                        open={true}
                        message="Are you sure you want to refetch FleetSync documents for this incident car?"
                        confirmAction={this.refetchFleetSyncDocuments}
                        action="Refetch Documents"
                        color="success"
                        loading={this.state.refetchFleetSyncDocumentsLoading}
                        error={this.state.refetchFleetSyncDocumentsError}
                        handleClose={() => this.setState({
                            refetchFleetSyncDocumentsModalOpen: false,
                            refetchFleetSyncDocumentsError: "",
                        })}
                    />
                )}
                <div className="incident-docs-layout">
                    <div className="incident-docs-sidebar">
                        <div className="incident-docs-sidebar-title">Documents</div>
                        <button
                            type="button"
                            className={`incident-docs-sidebar-item ${this.state.selectedSection === "general" ? "is-active" : ""}`}
                            onClick={() => this.setState({ selectedSection: "general" })}
                        >
                            <i className="fa fa-folder-open-o" aria-hidden="true" />
                            <span className="incident-docs-sidebar-label">Incident Documents</span>
                            <span className="incident-docs-sidebar-count">{counts.incident || 0}</span>
                        </button>
                        <button
                            type="button"
                            className={`incident-docs-sidebar-item ${this.state.selectedSection === "party" ? "is-active" : ""}`}
                            onClick={() => this.setState({ selectedSection: "party" })}
                        >
                            <i className="fa fa-users" aria-hidden="true" />
                            <span className="incident-docs-sidebar-label">Party Documents</span>
                            <span className="incident-docs-sidebar-count">{counts.party || 0}</span>
                        </button>
                        <button
                            type="button"
                            className={`incident-docs-sidebar-item ${this.state.selectedSection === "claim" ? "is-active" : ""}`}
                            onClick={() => this.setState({ selectedSection: "claim" })}
                        >
                            <i className="fa fa-balance-scale" aria-hidden="true" />
                            <span className="incident-docs-sidebar-label">Claim Documents</span>
                            <span className="incident-docs-sidebar-count">{counts.claim || 0}</span>
                        </button>
                        <button
                            type="button"
                            className={`incident-docs-sidebar-item ${this.state.selectedSection === "inspection" ? "is-active" : ""}`}
                            onClick={() => this.setState({ selectedSection: "inspection" })}
                        >
                            <i className="fa fa-camera" aria-hidden="true" />
                            <span className="incident-docs-sidebar-label">Inspection Documents</span>
                            <span className="incident-docs-sidebar-count">{counts.inspection || 0}</span>
                        </button>
                        <button
                            type="button"
                            className={`incident-docs-sidebar-item ${this.state.selectedSection === "driver" ? "is-active" : ""}`}
                            onClick={() => this.setState({ selectedSection: "driver" })}
                        >
                            <i className="fa fa-id-card-o" aria-hidden="true" />
                            <span className="incident-docs-sidebar-label">Driver Documents</span>
                            <span className="incident-docs-sidebar-count">{counts.driver || 0}</span>
                        </button>
                        <button
                            type="button"
                            className={`incident-docs-sidebar-item ${this.state.selectedSection === "car" ? "is-active" : ""}`}
                            onClick={() => this.setState({ selectedSection: "car" })}
                        >
                            <i className="fa fa-car" aria-hidden="true" />
                            <span className="incident-docs-sidebar-label">Car Documents</span>
                            <span className="incident-docs-sidebar-count">{(counts.car || 0) + (counts.fleetSyncCar || 0)}</span>
                        </button>
                    </div>
                    <div className="incident-docs-content">
                        {this.state.selectedSection === "car" && (
                            <div className="d-flex justify-content-between align-items-center mb-3">
                                <ul className="nav nav-tabs mb-0">
                                    <li className="nav-item">
                                        <button
                                            type="button"
                                            className={`nav-link ${this.state.selectedCarDocumentView === "car" ? "active" : ""}`}
                                            onClick={() => this.setState({ selectedCarDocumentView: "car" })}
                                        >
                                            BOS Car Documents ({counts.car || 0})
                                        </button>
                                    </li>
                                    <li className="nav-item">
                                        <button
                                            type="button"
                                            className={`nav-link ${this.state.selectedCarDocumentView === "fleetSync" ? "active" : ""}`}
                                            onClick={() => this.setState({ selectedCarDocumentView: "fleetSync" })}
                                        >
                                            FleetSync Car Documents ({counts.fleetSyncCar || 0})
                                        </button>
                                    </li>
                                </ul>
                                {showSyncFleetSyncDocuments && (
                                    <button
                                        type="button"
                                        className="btn btn-secondary documents-action-btn documents-action-btn-primary"
                                        onClick={() => this.setState({ refetchFleetSyncDocumentsModalOpen: true, refetchFleetSyncDocumentsError: "" })}
                                        disabled={this.state.refetchFleetSyncDocumentsLoading}
                                    >
                                        Refetch Documents
                                    </button>
                                )}
                            </div>
                        )}
                        <DocumentList
                            id={id}
                            isArchived={showArchiveFilter ? this.state.isArchived : false}
                            slugKey={selectedSectionConfig.slugKey}
                            documentOwnerMap={documentOwnerMap}
                            requireTargetSelection={true}
                            refetchQuery={refetchQuery || null}
                            viewType={viewType || null}
                            category={selectedSectionConfig.category}
                            showDocumentActions={showDocumentActions}
                            showIncidentDocuments={!selectedSectionConfig.showOtherDocuments}
                            showOtherDocumentsSection={selectedSectionConfig.showOtherDocuments}
                            otherDocumentsCategory={selectedSectionConfig.otherDocumentsCategory}
                            otherDocumentsCategoryId={selectedSectionConfig.otherDocumentsCategoryId}
                            incidentDocumentPreviewContext={selectedSectionConfig.incidentDocumentPreviewContext}
                            archiveFilterControl={showArchiveFilter ? this.renderArchiveViewSelector() : null}
                        />
                    </div>
                </div>
            </div>
        )
    }

    render() {
        const { mode } = this.props
        if (mode === "full") return this.renderFullIncidentView()
        return this.renderScopedView()
    }
}

export default graphql(IncidentDocumentOwnersQuery, {
    options: ({ id, mode }) => ({ variables: { incidentId: id, includeCounts: mode === "full" }, fetchPolicy: "network-only" }),
    props: ({ data: { incident, allIncidentParties, allLiabilityClaims, allSubrogationClaims } }) => ({
        allIncidentParties,
        allLiabilityClaims,
        allSubrogationClaims,
        incidentDriverId: incident?.driver?.id || null,
        incidentCarId: incident?.car?.id || null,
        documentCounts: {
            incident: incident?.incidentDocumentsCount || 0,
            party: incident?.partyDocumentsCount || 0,
            claim: incident?.claimDocumentsCount || 0,
            inspection: incident?.inspectionDocumentsCount || 0,
            driver: incident?.driverDocumentsCount || 0,
            car: incident?.carDocumentsCount || 0,
            fleetSyncCar: incident?.fleetSyncCarDocumentsCount || 0,
        },
    }),
})(graphql(HasPermissionsQuery, {
    options: {
        variables: {
            userPermissions: ["custom_can_refetch_car_documents_from_fleetsync"],
        },
    },
    props({ data: { hasPermissions } }) {
        return {
            hasPermissions,
        }
    },
})(withApolloClient(DocumentListContainer)))
